<template>
  <div class="container !pt-0">
    <el-form
      ref="integrationForm"
      :model="integrationForm"
      :rules="formRules"
      label-position="top"
      label-width="120px"
      @submit.native.prevent>
      <!-- Integration Name -->
      <el-row class="mb-6" :gutter="20">
        <el-col :span="24">
          <el-form-item label="Integration Name" prop="name">
            <el-input
              v-model="integrationForm.name"
              class="gray"
              name="omnisend-name"
              maxlength="255"/>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Omnisend API Key -->
      <el-row class="mb-4" :gutter="20">
        <el-col :span="24">
          <el-form-item label="API Key" prop="omnisend_integration.api_key">
            <el-input
              v-model="integrationForm.omnisend_integration.api_key"
              class="gray"
              name="omnisend-api-key"
              autocomplete="off"
              maxlength="255"/>
          </el-form-item>
        </el-col>
      </el-row>

      <div v-if="integrationCreated">
        <!-- Field Mapping -->
        <h3 class="mt-4 mb-2">Field Mapping</h3>
        <el-row class="mb-1" :gutter="20">
          <el-col :span="9">
            <el-form-item>
              <div slot="label" class="text-body-black">
                Internal Field
                <el-popover
                  placement="bottom"
                  trigger="hover"
                  content="This refers to the label names of the form elements used in your widget">
                  <strong slot="reference">
                    <i class="el-icon-info"></i>
                  </strong>
                </el-popover>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="2">
            <i class="el-icon-right text-xl opacity-0"></i>
          </el-col>
          <el-col :span="1">
            <el-checkbox
              v-model="allFieldsActive"
              @change="setAllFieldsActive(allFieldsActive)"/>
          </el-col>
          <el-col :span="11" :offset="1">
            <el-form-item>
              <div slot="label" class="text-body-black">
                External Field
                <el-popover
                  placement="bottom"
                  trigger="hover"
                  content="These are the field names that will be sent to Omnisend. Note: Email is mandatory.">
                  <strong slot="reference">
                    <i class="el-icon-info"></i>
                  </strong>
                </el-popover>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <hr class="my-2" />

        <el-row
          v-for="(mapping, index) in integrationForm.mapping"
          :key="index"
          :gutter="20"
          class="mb-2"
          type="flex"
          align="middle">
          <el-col :span="9">
            <span class="text-base">{{ mapping.internal.name }}</span>
          </el-col>
          <el-col :span="2">
            <i class="el-icon-right text-xl"></i>
          </el-col>
          <el-col :span="1">
            <el-checkbox
              v-model="mapping.active"
              @change="checkAllFieldsActive"/>
          </el-col>
          <el-col :span="11" :offset="1">
            <el-form-item>
              <el-select
                v-model="mapping.external.name"
                class="gray"
                :disabled="!mapping.active"
                placeholder="Select Omnisend Field">
                <el-option
                  v-for="field in omnisendFields"
                  :key="field.value"
                  :label="field.label"
                  :value="field.value"
                  :disabled="isFieldMapped(field.value)"/>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- Submit Button -->
      <div class="mt-8 flex justify-end">
        <button class="btn secondary outline big mr-4" @click="cancel">
          Cancel
        </button>
        <button class="btn primary big" @click="saveChanges">
          {{ integrationCreated ? 'Done' : 'Save' }}
        </button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import integrationAPI from '@api/integration'

export default {
  name: 'Omnisend',
  data() {
    return {
      enableUpdate: false,
      integrationCreated: false,
      integrationForm: {
        widget: null,
        name: 'Omnisend',
        mapping: [],
        target: 'OMNISEND',
        omnisend_integration: {
          api_key: '',
        },
      },
      formRules: {
        name: [
          { required: true, message: 'Integration name is required', trigger: 'blur' },
          { max: 255, message: 'Integration name must be less than 255 characters', trigger: 'blur' },
        ],
        'omnisend_integration.api_key': [
          { required: true, message: 'API Key is required', trigger: 'blur' },
        ],
      },
      formModified: false,
      omnisendFields: [], // store retrieved fields for mapping
      allFieldsActive: false,
    }
  },
  computed: {
    ...mapState('integration', ['targetIntegrationID']),
    ...mapGetters('campaign/widget', ['getWidgetFields']),
    integration() {
      return this.$store.getters['integration/targetIntegration']
    },
  },
  watch: {
    integrationForm: {
      handler() {
        // Only mark as modified once everything is initialized
        if (this.enableUpdate) {
          this.formModified = true
          this.$emit('form-modified', this.formModified)
        }
      },
      deep: true,
    },
    // Watch for changes to api_key instead of list_id
    // 'integrationForm.omnisend_integration.api_key'(newVal, oldVal) {
    //   if (this.integrationCreated && newVal && newVal !== oldVal) {
    //     this.fetchOmnisendFields()
    //   }
    // },
  },
  created() {
    // Initialize form from existing integration (if any)
    this.setFormValues(this.integration)
    if (this.targetIntegrationID) {
      this.integrationCreated = true
    }
    // Allow watchers to trigger changes after initial load
    this.$nextTick(() => {
      this.enableUpdate = true
    })
  },
  methods: {
    saveChanges() {
      this.$refs.integrationForm.validate((valid) => {
        if (!valid) return

        const action = this.integrationCreated
            ? 'integration/updateIntegration'
            : 'integration/createIntegration'

        this.$store.dispatch(action, this.integrationForm)
            .then(() => {
              this.$message.success('Save successful!')
              if (!this.integrationCreated) {
                this.integrationCreated = true
                this.fetchOmnisendFields()
              } else {
                // If updating an existing integration, you might close the drawer
                this.$emit('force-close')
              }
            })
            .catch((error) => {
              this.$message.error('Failed to save integration.')
              console.error(error)
            })
      })
    },
    setFormValues(integration) {
      if (integration) {
        this.integrationForm.name = integration.name || 'Omnisend'
        this.integrationForm.omnisend_integration.api_key =
            integration.omnisend_integration?.api_key || ''

        // Clone existing mappings (avoid mutating store directly)
        this.integrationForm.mapping = Array.isArray(integration.mapping)
            ? JSON.parse(JSON.stringify(integration.mapping))
            : []

        // Attempt fetching fields if there's an API key
        if (this.integrationForm.omnisend_integration.api_key) {
          this.fetchOmnisendFields()
        }
      }
      // Set widget from store
      this.integrationForm.widget =
          this.$store.state.campaign.widget.targetWidgetID

      // Ensure widget fields and mapping are in sync
      this.syncWidgetFieldsWithMapping()
    },
    syncWidgetFieldsWithMapping() {
      const widgetFields = this.getWidgetFields
      this.integrationForm.mapping = widgetFields.map((field) => {
        const existingMapping = this.integrationForm.mapping.find(
            (m) => m.internal.fieldID === field.id
        )
        return (
            existingMapping || {
              internal: { name: field.settings.label, fieldID: field.id },
              external: { name: '' },
              active: false,
            }
        )
      })
      this.checkAllFieldsActive()
    },
    setAllFieldsActive(active) {
      this.integrationForm.mapping.forEach((item) => {
        item.active = active
      })
    },
    checkAllFieldsActive() {
      this.allFieldsActive = this.integrationForm.mapping.every(
          (item) => item.active === true
      )
    },
    cancel() {
      if (this.formModified) {
        this.$emit('close-drawer')
      } else {
        this.$emit('force-close')
      }
    },
    /**
     * Fetch Omnisend fields based on the API key (list_id is not required for Omnisend)
     */
    fetchOmnisendFields() {
      const integrationId = this.integrationCreated ? this.integration.id : this.targetIntegrationID
      if (!integrationId) {
        this.$message.error('Integration ID is missing.')
        return
      }

      integrationAPI
          .fetchOmnisendSpecialFields(this.targetIntegrationID)
          .then((response) => {
            // Suppose the backend returns { special_fields: [{label: "Email", value: "email"}, ...] }
            this.omnisendFields = response.data.special_fields
          })
          .catch((error) => {
            this.$message.error('Failed to fetch Omnisend fields.')
            console.error(error)
          })
    },
    isFieldMapped(fieldValue) {
      return this.integrationForm.mapping.some(
          (mapping) => mapping.external.name === fieldValue && mapping.active
      )
    },
  },
}
</script>
